import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { axiosClientAuth } from '../../../../../app/utils'
import { KTIcon } from '../../../../helpers'
import Swal from 'sweetalert2'
import { useAuth } from '../../../../../app/modules/auth'
import { normalizeRoleKey } from '../../../../../app/utils/helper'

type RoleOption = { value: string; label: string }

/** Parse Django/DRF and common API error shapes (duplicate email, validation, etc.) */
function getApiErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data
  if (!data) {
    return error?.message || fallback
  }
  if (typeof data === 'string') return data

  const msg = data.message ?? data.error
  if (typeof msg === 'string' && msg.trim()) return msg
  if (Array.isArray(msg)) return msg.filter(Boolean).join(' ')

  if (typeof data.detail === 'string') return data.detail
  if (Array.isArray(data.detail)) return data.detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join(' ')

  if (data.email) {
    const e = data.email
    if (Array.isArray(e)) return e.join(' ')
    if (typeof e === 'string') return e
  }
  if (data.non_field_errors) {
    const e = data.non_field_errors
    if (Array.isArray(e)) return e.join(' ')
    if (typeof e === 'string') return e
  }
  if (data.password) {
    const e = data.password
    if (Array.isArray(e)) return e.join(' ')
    if (typeof e === 'string') return e
  }

  const nested = data.data?.message ?? data.data?.detail
  if (typeof nested === 'string') return nested

  try {
    return JSON.stringify(data)
  } catch {
    return fallback
  }
}

const EMAIL_ALREADY_IN_USE =
  'This email is already in use. Please use a different email address.'

/** When backend returns generic text like "Creation failed" for duplicate email, show clear copy */
function formatCreationErrorMessage(raw: string, error?: any): string {
  const t = (raw || '').trim()
  const lower = t.toLowerCase()

  if (
    /already\s+(exists|registered|in\s+use)|duplicate|email\s+.*(taken|used|registered)|user\s+with\s+this\s+email|unique\s+constraint/i.test(
      t
    )
  ) {
    return EMAIL_ALREADY_IN_USE
  }

  // Generic message your API returns for duplicate email
  const normalizedMsg = lower.replace(/[.!]+$/g, '').trim()
  if (normalizedMsg === 'creation failed') {
    return EMAIL_ALREADY_IN_USE
  }

  if (error?.response?.status === 409) {
    return EMAIL_ALREADY_IN_USE
  }

  return t
}

const ModalCreateCustomerSupport = ({ show, setShow, onSuccess }) => {
  const { currentUser, auth } = useAuth()
  const roleKey = normalizeRoleKey(
    currentUser?.role || auth?.role || currentUser?.user_type || auth?.user_type || ''
  )
  const isManager = roleKey === 'manager'

  const [input, setInput] = useState({
    email: '',
    password: '',
    role: '',
  })
  const [loading, setLoading] = useState(false)

  /** Manager: only Spectator + Customer Support (no Developer / Manager creation in UI) */
  const roleOptions: RoleOption[] = isManager
    ? [
        { value: 'Spectator', label: 'Spectator' },
        { value: 'CustomerSupport', label: 'Customer Support' },
      ]
    : [
        { value: 'CustomerSupport', label: 'Customer Support' },
        { value: 'Spectator', label: 'Spectator' },
        { value: 'Manager', label: 'Manager' },
      ]

  const resetForm = () => {
    setInput({
      email: '',
      password: '',
      role: '',
    })
  }

  const onChangeHandler = (e) => {
    const { name, value } = e.target
    setInput((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!input.email.trim() || !input.password.trim() || !input.role) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'Email, Password, and Role are required.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
      })
    }

    const allowedForManager = ['Spectator', 'CustomerSupport']
    if (isManager && !allowedForManager.includes(input.role)) {
      return Swal.fire({
        titleText: 'Not allowed',
        text: 'You can only create Spectator or Customer Support accounts.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
      })
    }

    try {
      setLoading(true)

      let endpoint = '/auth/useradmin/create-customer-support/'
      let successMsg = 'Customer Support account created successfully'

      if (input.role === 'Spectator') {
        endpoint = '/auth/useradmin/create-Spectator/'
        successMsg = 'Spectator account created successfully'
      } else if (input.role === 'Manager') {
        endpoint = '/auth/useradmin/create-manager/'
        successMsg = 'Manager account created successfully'
      }
      // CustomerSupport → default endpoint above

      const response = await axiosClientAuth.post(endpoint, {
        email: input.email,
        password: input.password,
      })

      if (response.data?.status) {
        Swal.fire({
          titleText: 'Success!',
          text: response.data.message || successMsg,
          icon: 'success',
          confirmButtonText: 'Ok',
        }).then(() => {
          resetForm()
          setShow(false)
          onSuccess?.()
        })
      } else {
        // 200 with status: false — e.g. email already registered
        const rawFail =
          (typeof response.data?.message === 'string' && response.data.message) ||
          getApiErrorMessage({ response: { data: response.data } }, `Failed to create ${input.role} account`)
        const failText = formatCreationErrorMessage(rawFail)
        Swal.fire({
          titleText: 'Error!',
          text: failText,
          icon: 'error',
          confirmButtonText: 'Ok! Got It',
        })
      }
    } catch (error: any) {
      console.error(`Error creating ${input.role}:`, error)

      const rawMessage = getApiErrorMessage(error, `Failed to create ${input.role} account`)
      const errorMessage = formatCreationErrorMessage(rawMessage, error)

      Swal.fire({
        titleText: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      resetForm()
      setShow(false)
    }
  }

  return (
    <Modal className='modal fade modal-lg' show={show} onHide={handleClose}>
      <div className='modal-header'>
        <h2 className='fw-bold'>Add Role</h2>
        <div className='d-flex justify-content-end'>
          <button
            className='ms-auto btn btn-sm btn-icon btn-active-color-primary'
            onClick={handleClose}
            disabled={loading}
          >
            <KTIcon iconName='cross' className='text-gray fs-1' />
          </button>
        </div>
      </div>
      <div className='modal-body scroll-y mx-5 mx-xl-15 my-7'>
        <form id='kt_modal_create_role_user_form' className='form' onSubmit={onSubmit}>
          {isManager ? (
            <p className='text-gray-600 fs-7 mb-8'>
              Assign <strong>Spectator</strong> or <strong>Customer Support</strong> to new users. You decide who gets which role.
            </p>
          ) : (
            <p className='text-gray-600 fs-7 mb-8'>
              Create staff accounts and assign <strong>Customer Support</strong>, <strong>Spectator</strong>, or{' '}
              <strong>Manager</strong> — you control which role each user receives.
            </p>
          )}
          <div
            className='d-flex flex-column scroll-y me-n7 pe-7'
            id='kt_modal_create_role_user_scroll'
            data-kt-scroll='true'
            data-kt-scroll-activate='{default: false, lg: true}'
            data-kt-scroll-max-height='auto'
            data-kt-scroll-dependencies='#kt_modal_create_role_user_header'
            data-kt-scroll-wrappers='#kt_modal_create_role_user_scroll'
            data-kt-scroll-offset='300px'
          >
            <div className='fv-row mb-7'>
              <label className='required fw-semibold fs-6 mb-2'>Email</label>
              <input
                type='email'
                name='email'
                className='form-control form-control-solid mb-3 mb-lg-0'
                placeholder='example@domain.com'
                value={input.email}
                onChange={onChangeHandler}
                disabled={loading}
                required
              />
            </div>
            <div className='fv-row mb-7'>
              <label className='required fw-semibold fs-6 mb-2'>Password</label>
              <input
                type='password'
                name='password'
                className='form-control form-control-solid mb-3 mb-lg-0'
                placeholder='Enter password'
                value={input.password}
                onChange={onChangeHandler}
                disabled={loading}
                required
              />
            </div>
            <div className='fv-row mb-7'>
              <label className='required fw-semibold fs-6 mb-2'>Role</label>
              <select
                className='form-control form-control-solid mb-3 mb-lg-0'
                name='role'
                value={input.role}
                onChange={onChangeHandler}
                disabled={loading}
                required
              >
                <option value=''>Select role</option>
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='text-center pt-15'>
            <button
              type='button'
              className='btn btn-light me-3'
              onClick={handleClose}
              disabled={loading}
            >
              Discard
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading}
              data-kt-indicator={loading ? 'on' : 'off'}
              data-kt-users-modal-action='submit'
            >
              <span className='indicator-label'>Submit</span>
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default ModalCreateCustomerSupport
